import { useEffect, useState, useRef } from 'react'
import { motion } from 'framer-motion'
import { useTranslation, Trans } from 'react-i18next'
import { FaImages } from 'react-icons/fa'
import LoadingSpinner from '../components/LoadingSpinner'
import ConsentCheckbox from '../components/ConsentCheckbox'
import ScrollToTopButton from '../components/ScrollToTopButton'
import MobileQuoteButton from '../components/MobileQuoteButton'
import MondayManagement from '../pages/admin/MondayManagement'
import GalleryModal from '../components/GalleryModal'
import { useGalleries, useGallery } from '../lib/galleries'

const FORM_THANK_YOU_MAP = {
  impact_windows_new_project: '/forms/impact-windows/new-quote/thank-you',
}

const HERO_IMAGE = '/impact-windows/1-hero.webp'
const BENEFITS_VIDEO = '/impact-windows/2-why-impact-windows.mp4'
const FORM_SIDE_IMAGE = '/impact-windows/form-side.webp'

const BENEFIT_ICONS = [
  '/impact-windows/icon-1-protect.webp',
  '/impact-windows/icon-2-efficiency.webp',
  '/impact-windows/icon-3-value.webp',
  '/impact-windows/icon-4-compliance.webp',
]

const ImpactWindowsSection = () => {
  const [isLoading, setIsLoading] = useState(true)
  const [videoError, setVideoError] = useState(false)
  const [showMondayForm, setShowMondayForm] = useState(false)
  const [activeFormType, setActiveFormType] = useState(null)
  const preModalPath = useRef(null)
  const { t } = useTranslation()
  const [galleryOpen, setGalleryOpen] = useState(false)
  const [activeGalleryKey, setActiveGalleryKey] = useState(null)
  const { galleries } = useGalleries()
  const { images: activeGalleryImages, loading: galleryLoading } = useGallery(activeGalleryKey)
  const windowsGallery = galleries.find(g => g.service_key === 'windows')

  const FORM_PATHS = { construction_new_project: '/forms/impact-windows/new-quote' }

  const openModal = (formType = 'construction_new_project') => {
    preModalPath.current = window.location.pathname
    window.history.pushState({ modal: true }, '', FORM_PATHS[formType] || '/forms/impact-windows/new-quote')
    setActiveFormType(formType)
    setShowMondayForm(true)
  }

  const closeModal = () => {
    setShowMondayForm(false)
    window.history.replaceState(null, '', preModalPath.current || '/impact-windows')
  }

  useEffect(() => {
    const handlePopState = () => setShowMondayForm(false)
    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  const benefitsList = t('windows.benefits_list', { returnObjects: true })
  const benefits = Array.isArray(benefitsList) ? benefitsList.map((text, index) => ({
    text,
    icon: BENEFIT_ICONS[index]
  })) : []

  const servicesList = t('windows.services_list', { returnObjects: true })
  const safeServicesList = Array.isArray(servicesList) ? servicesList : []

  useEffect(() => {
    const preloadImages = async () => {
      try {
        const imagesToLoad = [HERO_IMAGE, FORM_SIDE_IMAGE, ...BENEFIT_ICONS]
        await Promise.all(
          imagesToLoad.map((src) => {
            return new Promise((resolve, reject) => {
              const img = new Image()
              img.src = src
              img.onload = resolve
              img.onerror = reject
            })
          })
        )
      } catch (error) {
        console.error('Failed to preload images', error)
      } finally {
        setIsLoading(false)
      }
    }

    preloadImages()
  }, [])

  if (isLoading) {
    return <LoadingSpinner />
  }

  return (
    <section id='impact-windows' className='bg-white text-black' aria-labelledby='impact-windows-heading'>
      {/* Header Section */}
      <div className='relative min-h-screen sm:h-screen sm:max-h-[1080px] w-full overflow-hidden'>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2 }}
          className='absolute inset-0 w-full h-full'
        >
          <img src={HERO_IMAGE} alt={t('windows.hero_alt')} className='h-full w-full object-cover' />
        </motion.div>
        <div className='absolute inset-0 bg-gradient-to-r to-transparent from-black/90 via-black/40' />

        <div className='relative z-10 h-full max-w-[1920px] mx-auto px-6 sm:px-12 lg:px-20 py-24 flex flex-col justify-end sm:justify-between'>
          <div className='max-w-4xl'>
            <motion.h1
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: 'easeOut', delay: 0.2 }}
              id='impact-windows-heading'
              className='text-4xl sm:text-7xl md:text-8xl lg:text-[7rem] font-black leading-[0.9] tracking-tighter uppercase text-white mb-8 sm:mb-12 lg:mb-16'
            >
              {t('windows.hero_title')}
            </motion.h1>
          </div>

          <div className='flex flex-wrap items-end justify-between w-full gap-6'>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.4, ease: 'easeOut' }}
              className='max-w-2xl text-xl font-medium leading-relaxed text-white sm:text-2xl'
            >
              <Trans
                i18nKey="windows.hero_text_1"
                components={{ 1: <span className="font-bold" />, 3: <span className="font-bold" /> }}
              />
              <br className="hidden sm:block" />
              <br className="hidden sm:block" />
              <Trans
                i18nKey="windows.hero_text_2"
                components={{ 1: <span className="font-bold" />, 3: <span className="font-bold" />, 5: <span className="font-bold" /> }}
              />
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, ease: 'easeOut', delay: 0.6 }}
              className='hidden sm:flex flex-shrink-0'
            >
              <button
                onClick={() => openModal('construction_new_project')}
                className='inline-flex justify-center px-8 py-4 text-sm font-bold tracking-widest uppercase rounded-lg bg-primary text-black hover:bg-yellow-400 min-w-[220px]'
              >
                {t('windows.cta')}
              </button>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className='relative min-h-screen flex items-center overflow-hidden'>
        <div className='absolute inset-0'>
          {!videoError ? (
            <video
              className='h-full w-full object-cover'
              autoPlay
              loop
              muted
              playsInline
              onError={() => setVideoError(true)}
            >
              <source src={BENEFITS_VIDEO} type='video/mp4' />
            </video>
          ) : (
            <img src={HERO_IMAGE} alt={t('windows.hero_alt')} className='h-full w-full object-cover' />
          )}
          <div className='absolute inset-0 bg-black/70' />
        </div>

        <div className='relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 w-full'>
          <motion.h2
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className='text-center text-white text-6xl sm:text-7xl md:text-8xl lg:text-[7rem] font-black leading-[0.9] tracking-tighter uppercase mb-24'
          >
            {t('windows.benefits_title')}
          </motion.h2>

          <motion.div
            initial='hidden'
            whileInView='visible'
            viewport={{ once: true }}
            variants={{
              visible: { transition: { staggerChildren: 0.2 } },
            }}
            className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8'
          >
            {benefits.map((benefit) => (
              <motion.div
                key={benefit.text}
                variants={{
                  hidden: { opacity: 0, y: 30 },
                  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
                }}
                className='text-center text-white'
              >
                <img src={benefit.icon} alt={benefit.text} className='w-20 h-20 mx-auto mb-6' loading='lazy' />
                <p className='text-xl sm:text-2xl font-normal max-w-[260px] mx-auto leading-tight'>{benefit.text}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Services Section */}
      <div className='py-20 px-4 sm:px-6 lg:px-8'>
        <div className='max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center'>
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className='relative inline-block mb-8 overflow-hidden'>
              <motion.div
                initial={{ width: 0 }}
                whileInView={{ width: '100%' }}
                transition={{ duration: 1.0, ease: 'easeOut' }}
                viewport={{ once: true }}
                className='absolute left-0 top-0 h-full bg-primary z-0'
              />
              <h3 className='text-3xl font-bold relative z-10 pr-4 py-1'>{t('windows.services_title')}</h3>
            </div>

            <ul className='space-y-4 text-base leading-relaxed'>
              {safeServicesList.map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className='mb-6'
                >
                  <h4 className='text-xl font-bold mb-2 flex items-center gap-3'>
                    <span className='w-2 h-2 rounded-full bg-primary' />
                    {service.title}
                  </h4>
                  <ul className='pl-5 space-y-1 text-gray-700'>
                    {service.bullets && service.bullets.map((bullet, idx) => (
                      <li key={idx} className='list-disc'>{bullet}</li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </ul>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.6 }}
              className='mt-8 flex flex-wrap gap-4'
            >
              <button
                onClick={() => openModal('construction_new_project')}
                className='inline-flex justify-center px-8 py-4 text-sm font-bold tracking-widest uppercase rounded-lg bg-primary text-black hover:bg-yellow-400 min-w-[220px]'
              >
                {t('windows.cta')}
              </button>
              {windowsGallery && (
                <button
                  onClick={() => { setActiveGalleryKey('windows'); setGalleryOpen(true) }}
                  className='inline-flex items-center gap-2 justify-center px-8 py-4 text-sm font-bold tracking-widest uppercase rounded-lg border-2 border-black text-black hover:bg-black hover:text-white transition-all duration-200 min-w-[220px]'
                >
                  <FaImages />
                  {t('construction.view_gallery')}
                </button>
              )}
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className='flex justify-center'
          >
            <img src={FORM_SIDE_IMAGE} alt={t('windows.house_alt', 'Impact Windows House')} className='w-full max-w-md shadow-2xl' loading='lazy' />
          </motion.div>
        </div>
      </div>

      {/* Form Section */}
      <div className='px-4 py-24 bg-gray-50 sm:px-6 lg:px-8'>
        <div className="max-w-7xl mx-auto bg-white rounded-[3rem] p-8 sm:p-16 shadow-2xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className='mb-16 text-center'
          >
            <h2 className='mb-4 text-4xl font-black text-black sm:text-5xl'>{t('windows.form_title')}</h2>
            <p className='text-xl text-gray-500'>{t('windows.form_subtitle')}</p>
          </motion.div>

          <div className='grid gap-12 lg:grid-cols-2'>
            <form
              action="https://formsubmit.co/info@jdrivero.com"
              method="POST"
              className="contents"
            >
              <input type="hidden" name="_subject" value={t('form.subjects.windows', 'New Impact Windows Quote Request')} />
              <input type="hidden" name="_template" value="table" />
              <input type="hidden" name="_next" value="https://www.jdrivero.com/success" />
              <input type="hidden" name="_captcha" value="false" />

              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className='space-y-8'
              >
                <div className='grid gap-6 sm:grid-cols-2'>
                  <label className='block space-y-3'>
                    <span className='text-xs font-bold tracking-widest text-gray-400 uppercase'>{t('form.first_name')}</span>
                    <input
                      type='text'
                      name={t('form.first_name_label', 'First Name')}
                      className='py-3 w-full text-lg bg-gray-50 border-b-2 border-gray-200 transition-colors outline-none focus:border-primary'
                      required
                    />
                  </label>
                  <label className='block space-y-3'>
                    <span className='text-xs font-bold tracking-widest text-gray-400 uppercase'>{t('form.last_name')}</span>
                    <input
                      type='text'
                      name={t('form.last_name_label', 'Last Name')}
                      className='py-3 w-full text-lg bg-gray-50 border-b-2 border-gray-200 transition-colors outline-none focus:border-primary'
                      required
                    />
                  </label>
                </div>

                <label className='block space-y-3'>
                  <span className='text-xs font-bold tracking-widest text-gray-400 uppercase'>{t('form.email')}</span>
                  <input
                    type='email'
                    name={t('form.email_label', 'Email')}
                    className='py-3 w-full text-lg bg-gray-50 border-b-2 border-gray-200 transition-colors outline-none focus:border-primary'
                    required
                  />
                </label>

                <label className='block space-y-3'>
                  <span className='text-xs font-bold tracking-widest text-gray-400 uppercase'>{t('form.phone')}</span>
                  <input
                    type='tel'
                    name={t('form.phone_label', 'Phone Number')}
                    pattern="^[+]?[0-9 \\-]{10,15}$"
                    title={t('form.phone_error', 'Phone number must be between 10 and 15 digits.')}
                    className='py-3 w-full text-lg bg-gray-50 border-b-2 border-gray-200 transition-colors outline-none focus:border-primary'
                    required
                  />
                </label>

                <label className='block space-y-3'>
                  <span className='text-xs font-bold tracking-widest text-gray-400 uppercase'>{t('form.project_details')}</span>
                  <textarea
                    name={t('form.project_details_label', 'Project Details')}
                    rows={4}
                    className='py-3 w-full text-lg bg-gray-50 border-b-2 border-gray-200 transition-colors outline-none resize-none focus:border-primary'
                    required
                  />
                </label>
                <ConsentCheckbox />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className='p-8 space-y-8 rounded-3xl bg-primary/10'
              >
                <fieldset className='space-y-6'>
                  <legend className='mb-6 text-sm font-bold tracking-widest text-black uppercase'>{t('form.type_label')}</legend>
                  {['Residential', 'Commercial', 'Civil'].map((typeKey) => (
                    <label key={typeKey} className='flex gap-4 items-center cursor-pointer group'>
                      <div className='flex relative justify-center items-center w-6 h-6 bg-white rounded-full border-2 border-gray-300 transition-colors group-hover:border-primary'>
                        <input type='radio' name='projectType' value={typeKey} required className='absolute inset-0 w-full h-full appearance-none cursor-pointer peer' />
                        <div className='w-3 h-3 rounded-full transition-transform scale-0 bg-primary peer-checked:scale-100' />
                      </div>
                      <span className='text-lg font-medium text-gray-700 transition-colors group-hover:text-black'>
                        {t(`form.${typeKey.toLowerCase()}`)}
                      </span>
                    </label>
                  ))}
                </fieldset>

                <fieldset className='space-y-6'>
                  <legend className='mb-6 text-sm font-bold tracking-widest text-black uppercase'>{t('form.description_label')}</legend>
                  {['New construction', 'Replacement / Renovation'].map((typeKey) => (
                    <label key={typeKey} className='flex gap-4 items-center cursor-pointer group'>
                      <div className='flex relative justify-center items-center w-6 h-6 bg-white rounded-full border-2 border-gray-300 transition-colors group-hover:border-primary'>
                        <input type='radio' name='description' value={typeKey} required className='absolute inset-0 w-full h-full appearance-none cursor-pointer peer' />
                        <div className='w-3 h-3 rounded-full transition-transform scale-0 bg-primary peer-checked:scale-100' />
                      </div>
                      <span className='text-lg font-medium text-gray-700 transition-colors group-hover:text-black'>
                        {typeKey === 'New construction' ? t('form.new_construction') : t('form.replacement')}
                      </span>
                    </label>
                  ))}
                </fieldset>

                <button type="submit" className="w-full rounded-2xl bg-primary hover:bg-yellow-400 px-8 py-5 text-black font-black uppercase tracking-[0.2em] shadow-lg transition-all hover:scale-[1.02]">
                  {t('form.send')}
                </button>
              </motion.div>
            </form>
          </div>
        </div>
      </div>
      <GalleryModal images={activeGalleryImages.map(img => img.url)} isOpen={galleryOpen} onClose={() => setGalleryOpen(false)} loading={galleryLoading} />
      <MobileQuoteButton onClick={() => openModal('construction_new_project')} />
      <ScrollToTopButton />

      {/* Monday Form Modal */}
      {showMondayForm && (
        <div className="overflow-y-auto fixed inset-0 z-50">
          <div className="flex justify-center items-center p-4 min-h-screen">
            <div
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
              onClick={closeModal}
            />
            <div className="relative bg-white rounded-2xl shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
              <div className="overflow-y-auto max-h-[90vh]">
                <MondayManagement
                  session={null}
                  onReturn={closeModal}
                  isPublicMode={true}
                  defaultFormType={activeFormType}
                  thankYouPath={FORM_THANK_YOU_MAP[activeFormType]}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}

export default ImpactWindowsSection
